import { location, onMount, reactive } from 'cdui-js';
import i18n from '../../../i18n';
import { DiFiInput } from '../../../components/DiFi/DiFiInput';
import { showToast } from '../components/Toast';
import { goBackByBrowser } from '../../../lib/browser';
import { StiFormField } from './FormField';
import { StockTransferNav } from './Nav';
import {
  createStockLineId,
  defaultTransferMarketId,
  getStockTransferDraft,
  getStockTransferLines,
  marketTypeForTransfer,
  readMarketQuery,
  readStockLineIdQuery,
  sanitizeTransferQuantityInput,
  sanitizeTransferStockCodeInput,
  sanitizeTransferStockNameInput,
  setStockTransferLines,
  stockLineKey,
  syncStockTransferAppShell,
  TRANSFER_STOCK_CODE_MAX_LEN,
  TRANSFER_STOCK_NAME_MAX_LEN,
  withStockTransferQuery,
  type StockTransferLineItem,
  type StockTransferMarket,
} from './shared';
import './page.css';

export const StockTransferInStockAdd = () => {
  const state = reactive({
    market: 'HK' as StockTransferMarket,
    editLineId: '',
    stockNameInput: '',
    stockCodeInput: '',
    quantity: '',
  });

  const canConfirm = () => {
    const qtyStr = String(state.quantity);
    if (!/^\d+$/.test(qtyStr)) return false;
    const qty = parseInt(qtyStr, 10);
    return (
      !!state.stockNameInput.trim() &&
      !!state.stockCodeInput.trim() &&
      Number.isFinite(qty) &&
      qty >= 1
    );
  };

  const onConfirm = () => {
    const name = state.stockNameInput.trim();
    if (!name) {
      showToast(i18n.StockTransferIn.PleaseInputStockName);
      return;
    }
    const code = state.stockCodeInput.trim();
    if (!code) {
      showToast(i18n.StockTransferIn.PleaseInputStockCode);
      return;
    }
    const qtyStr = String(state.quantity);
    if (!/^\d+$/.test(qtyStr)) {
      showToast(i18n.StockTransferIn.PleaseInputStockQty);
      return;
    }
    const qty = parseInt(qtyStr, 10);
    if (!Number.isFinite(qty) || qty < 1) {
      showToast(i18n.StockTransferIn.PleaseInputStockQty);
      return;
    }
    const lines = getStockTransferLines().slice();
    const nextItem: StockTransferLineItem = {
      id: state.editLineId || createStockLineId(),
      stockCode: code,
      marketId: defaultTransferMarketId(state.market),
      stockName: name,
      marketType: marketTypeForTransfer(state.market),
      quantity: qty,
      selected: true,
    };
    const key = stockLineKey(nextItem);
    const dup = lines.find((item) => item.id !== nextItem.id && stockLineKey(item) === key);
    if (dup) {
      showToast(i18n.StockTransferIn.StockDuplicate);
      return;
    }
    const idx = lines.findIndex((item) => item.id === nextItem.id);
    if (idx >= 0) {
      const prev = lines[idx];
      lines[idx] = { ...nextItem, selected: prev.selected };
    } else {
      lines.push(nextItem);
    }
    setStockTransferLines(lines);
    location.routeTo(withStockTransferQuery('/h5/stock-transfer-in/stocks', { market: state.market }), [0, 0]);
  };

  onMount(() => {
    syncStockTransferAppShell();
    state.market = readMarketQuery();
    state.editLineId = readStockLineIdQuery();
    if (!getStockTransferDraft()) {
      location.routeTo('/h5/stock-transfer-in');
      return;
    }
    if (state.editLineId) {
      const line = getStockTransferLines().find((item) => item.id === state.editLineId);
      if (line) {
        state.stockNameInput = sanitizeTransferStockNameInput(line.stockName?.trim() || '');
        state.stockCodeInput = sanitizeTransferStockCodeInput(line.stockCode?.trim() || '');
        state.quantity = sanitizeTransferQuantityInput(String(line.quantity));
      }
    }
  });

  return (
    <div class="stock-transfer-in-h5">
      <StockTransferNav title={i18n.StockTransferIn.AddStockFormTitle} onBack={goBackByBrowser} />
      <div class="sti-scroll">
        <div class="sti-form">
          <StiFormField label={i18n.StockTransferIn.StockNameLabel}>
            <DiFiInput
              shellClass="f-input-2"
              inputKind="text"
              maxLength={TRANSFER_STOCK_NAME_MAX_LEN}
              placeholder={i18n.StockTransferIn.StockNamePlaceholder}
              value={state.stockNameInput}
              onUpdateValue={(v) => {
                state.stockNameInput = sanitizeTransferStockNameInput(v);
              }}
            />
          </StiFormField>
          <StiFormField label={i18n.StockTransferIn.StockCodeLabel}>
            <DiFiInput
              shellClass="f-input-2"
              inputKind="text"
              maxLength={TRANSFER_STOCK_CODE_MAX_LEN}
              inputMode="text"
              placeholder={i18n.StockTransferIn.StockCodePlaceholder}
              value={state.stockCodeInput}
              pattern={/^[A-Za-z0-9.\-_/]*$/}
              onUpdateValue={(v) => {
                state.stockCodeInput = sanitizeTransferStockCodeInput(v);
              }}
            />
          </StiFormField>
          <StiFormField
            label={i18n.StockTransferIn.StockQtyLabel}
            hint={i18n.StockTransferIn.StockQtyNote}
            hintTone="warn"
          >
            <DiFiInput
              shellClass="f-input-2"
              inputKind="digit"
              maxLength={12}
              inputMode="numeric"
              placeholder={i18n.StockTransferIn.StockQtyPlaceholder}
              value={state.quantity}
              onUpdateValue={(v) => {
                state.quantity = sanitizeTransferQuantityInput(v);
              }}
            />
          </StiFormField>
        </div>
      </div>
      <div class="sti-footer">
        <button
          type="button"
          class={`primary-btn btn-rounded width-100${canConfirm() ? '' : ' sti-btn-disabled'}`}
          disabled={!canConfirm()}
          onclick={onConfirm}
        >
          {i18n.StockTransferIn.Confirm}
        </button>
      </div>
    </div>
  );
};
